import React, { useEffect, useState } from 'react';
import { Typography, Button, Dialog, DialogTitle, DialogContent, IconButton, FormControl, Select, MenuItem, Grid, Card, CardContent, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, DragIndicator, Tune as FormatIcon, Info as InfoIcon } from '@mui/icons-material';
import { TemplateReportService } from '../../../services/templateReportService';
import { openPopup } from '../../../stateManagement/actions/popupActions';
import "./TemplateReports.css";
import Input from '../../Input/Input';
import { DATE_FORMAT_OPTIONS, NUMBER_FORMAT_OPTIONS, PLACEHOLDERS } from './constants';
import { UI_CONSTANTS } from './constants';
import { useNavigate } from 'react-router-dom';
import { Stepper, Step, StepLabel, Chip } from '@mui/material';

// TypeScript interfaces for type safety
export interface TemplateColumnMeta {
  label: string;
  type: string;
  default?: boolean;
}

export interface ReportColumn {
  id: string;
  customHeader: string;
  format: string;
  position: number;
  hasError?: boolean;
}

export interface Report {
  id: string;
  name: string;
  type?: string;
  format?: string;
  baseQuery?: string;
  fetchLimit?: string;
  designations?: string;
  implementation?: string;
  reportDelimiter?: string;
  extendedAttributes: {
    templateName?: string;
    headers?: Array<{ key: string; value: string; position: string }>;
    columns?: Array<{ id: string; format: string }>;
  };
  filter?: unknown;
  globalFilters?: unknown;
  headers?: any[]; // <-- add this line
}

export interface TemplateData {
  name: string;
  all_columns?: Record<string, TemplateColumnMeta>;
  allColumns?: Record<string, TemplateColumnMeta>;
}

export interface ReportColumnData {
  id: string;
  format?: string;
  type?: string;
}

export interface ReportHeaderData {
  key: string;
  value: string;
  position: string;
}

// Rename component
const ManageReportTemplateBased: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editState, setEditState] = useState<Report | null>(null);
  const [templateColumns, setTemplateColumns] = useState<Record<string, TemplateColumnMeta>>({});
  const [selectedColumns, setSelectedColumns] = useState<ReportColumn[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [expandedFormat, setExpandedFormat] = useState<string | null>(null);
  const [editingColumnNumber, setEditingColumnNumber] = useState<number | null>(null);
  const [newColumnPosition, setNewColumnPosition] = useState<string>("");
  const [formatErrors, setFormatErrors] = useState<Record<string, string>>({});
  const [templateLoading, setTemplateLoading] = useState<boolean>(false);
  const [newColumnToAdd, setNewColumnToAdd] = useState<string>("");
  const [showCustomDateFormat, setShowCustomDateFormat] = useState<Record<string, boolean>>({});
  const [showCustomNumberFormat, setShowCustomNumberFormat] = useState<Record<string, boolean>>({});
  const [customDateFormat, setCustomDateFormat] = useState<Record<string, string>>({});
  const [customNumberFormat, setCustomNumberFormat] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await TemplateReportService.getEditableReports();
      setReports(data);
    } catch (error) {
      openPopup('Error', 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplate = async (templateName: string) => {
    setTemplateLoading(true);
    try {
      // Use the same approach as create report - fetch all templates and find the specific one
      const allTemplates = await TemplateReportService.fetchTemplates();
      
      const foundTemplate = allTemplates.find((t: TemplateData) => t.name === templateName);
      
      if (foundTemplate && foundTemplate.all_columns) {
        setTemplateColumns(foundTemplate.all_columns || {});
      } else {
        // Try direct template fetch as fallback
        try {
          const directTemplate = await TemplateReportService.getTemplate(templateName);
          
          if (directTemplate && (directTemplate as TemplateData).allColumns) {
            setTemplateColumns((directTemplate as TemplateData).allColumns || {});
          } else if (directTemplate && (directTemplate as TemplateData).all_columns) {
            setTemplateColumns((directTemplate as TemplateData).all_columns || {});

          } else {

            setTemplateColumns({});
          }
        } catch (fallbackError) {

          setTemplateColumns({});
        }
      }
    } catch (error) {

      openPopup('Error', `Failed to load template: ${templateName}`);
      setTemplateColumns({});
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleEdit = async (report: Report) => {
    try {
      setEditState({ ...report });
      setEditDialogOpen(true);
      
      // Extract template name from extended attributes
      const templateName = report.extendedAttributes?.templateName;
      
      if (templateName) {
        await fetchTemplate(templateName);
      } else {
        openPopup('Error', 'No template name found for this report');
        setTemplateColumns({});
      }
      
      // Map report headers to selectedColumns state (new structure)
      const headers = (report.headers || report.extendedAttributes?.headers || []) as any[];
      const mappedColumns = headers
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((header: any) => ({
          id: header.key,
          customHeader: header.value,
          format: header.format,
          position: header.position,
          type: header.type,
        }));
      setSelectedColumns(mappedColumns);
    } catch (error) {
      openPopup('Error', 'Failed to load report for editing');
    }
  };

  const handleHeaderChange = (columnId: string, value: string) => {
    // Prevent duplicate headers
    const isDuplicate = selectedColumns.some(col => col.id !== columnId && col.customHeader.toLowerCase() === value.toLowerCase());
    setSelectedColumns(cols => cols.map(col => col.id === columnId ? { ...col, customHeader: value, hasError: isDuplicate } : col));
    setErrorMessage(isDuplicate ? 'Duplicate header names are not allowed.' : '');
  };

  const handleFormatChange = (columnId: string, value: string) => {
    setSelectedColumns(cols => cols.map(col => col.id === columnId ? { ...col, format: value } : col));
  };

  const handleAddColumn = (columnKey: string) => {
    if (selectedColumns.some(col => col.id === columnKey)) return;
    const meta = templateColumns[columnKey];
    if (!meta) return;

    setSelectedColumns(cols => [...cols, {
      id: columnKey,
      customHeader: meta.label || columnKey,
      format: meta.type || 'string',
      position: cols.length + 1,
    }]);
    setNewColumnToAdd(""); // Reset dropdown
  };

  const handleRemoveColumn = (columnId: string) => {
    setSelectedColumns(cols => cols.filter(col => col.id !== columnId));
  };

  const handleSave = async () => {
    if (selectedColumns.some(col => col.hasError)) {
      setErrorMessage('Duplicate header names are not allowed.');
      return;
    }
    if (!editState) {
      setErrorMessage('No report selected for editing.');
      return;
    }
    try {
      // Build legacy config for update (same as create flow)
      const legacyConfig = {
        name: editState.name,
        template: editState.extendedAttributes?.templateName || '',
        columns: selectedColumns.map((col) => {
          const type = templateColumns[col.id]?.type || 'string';
          return {
            originalColumn: col.id,
            displayName: col.customHeader,
            customHeader: col.customHeader,
            columnType: type,
            dateFormat: type === 'date' || type === 'datetime' ? col.format : undefined,
            numberFormat: type === 'number' ? col.format : undefined,
            textFormat: type === 'string' ? col.format : undefined,
          };
        }),
        query: editState.baseQuery || '',
        isEmail: false,
      };
      const templateObj = {
        id: legacyConfig.template,
        name: legacyConfig.template,
        type: 'TemplateReport',
        role: 'admin',
        filter: null,
        globalFilters: null,
      };
      await TemplateReportService.createReport(legacyConfig, templateObj, false);
      openPopup('Success', 'Report updated successfully');
      setEditDialogOpen(false);
      setEditState(null);
      fetchReports();
    } catch (error) {
      openPopup('Error', 'Failed to update report');
    }
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!draggedColumn) return;
    const sourceIndex = selectedColumns.findIndex((col) => col.id === draggedColumn);
    if (sourceIndex === -1 || sourceIndex === targetIndex) return;
    const newColumns = [...selectedColumns];
    const [movedColumn] = newColumns.splice(sourceIndex, 1);
    newColumns.splice(targetIndex, 0, movedColumn);
    setSelectedColumns(newColumns);
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  const handleNumberClick = (index: number) => {
    setEditingColumnNumber(index);
    setNewColumnPosition((index + 1).toString());
  };

  const handlePositionChange = (currentIndex: number, newPosition: string) => {
    if (!newPosition.trim()) {
      setEditingColumnNumber(null);
      return;
    }
    const newPos = parseInt(newPosition);
    if (isNaN(newPos) || newPos < 1 || newPos > selectedColumns.length) {
      setEditingColumnNumber(null);
      return;
    }
    const newIndex = newPos - 1;
    if (newIndex === currentIndex) {
      setEditingColumnNumber(null);
      return;
    }
    const newColumns = [...selectedColumns];
    const [movedColumn] = newColumns.splice(currentIndex, 1);
    newColumns.splice(newIndex, 0, movedColumn);
    setSelectedColumns(newColumns);
    setEditingColumnNumber(null);
  };

  const handlePositionInputBlur = () => {
    if (editingColumnNumber !== null) {
      handlePositionChange(editingColumnNumber, newColumnPosition);
    }
  };

  const handlePositionInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingColumnNumber !== null) {
        handlePositionChange(editingColumnNumber, newColumnPosition);
      }
    } else if (e.key === 'Escape') {
      setEditingColumnNumber(null);
    }
  };

  const handleFormatExpand = (columnId: string) => {
    setExpandedFormat(expandedFormat === columnId ? null : columnId);
    setFormatErrors(prev => ({ ...prev, [columnId]: '' }));
  };

  const getAvailableTemplateColumns = () => {
    const selectedColumnIds = selectedColumns.map(col => col.id);
    return Object.entries(templateColumns)
      .filter(([key]) => !selectedColumnIds.includes(key))
      .map(([key, meta]) => ({ key, meta }));
  };

  // Helper to get column type
  const getColumnType = (col: ReportColumnData) => {
    return templateColumns[col.id]?.type || col.type || '';
  };

  return (
    <div className="template-reports-container">
      <div className="template-reports-header" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px 32px',
        background: 'linear-gradient(90deg, #f8fafc 60%, #e0f7f4 100%)',
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(36,198,177,0.10)',
        marginBottom: 24,
      }}>
        <div>
          <Typography variant="h4" component="h1" className="page-title" style={{ marginBottom: 4 }}>
            Manage Reports (Template-based)
          </Typography>
          <Typography variant="subtitle1" style={{ color: '#64748b', fontWeight: 400 }}>
            View, edit, and create template-based reports.
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          style={{
            background: 'linear-gradient(90deg, #24c6b1 60%, #189e91 100%)',
            color: '#fff',
            fontWeight: 700,
            borderRadius: 8,
            fontSize: 16,
            padding: '12px 32px',
            boxShadow: '0 2px 8px rgba(36,198,177,0.10)'
          }}
          onClick={() => navigate('/dashboard/manageTemplateReports/create')}
          startIcon={<AddIcon />}
        >
          Create Report
        </Button>
      </div>
      {loading ? (
        <Typography>Loading reports...</Typography>
      ) : reports.length === 0 ? (
        <Typography>No editable reports found.</Typography>
      ) : (
        <Grid container spacing={3}>
          {reports.map((report) => (
            <Grid item xs={12} sm={6} md={4} key={report.id || Math.random()}>
              <Card
                elevation={3}
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 16,
                  boxShadow: '0 4px 16px 0 rgba(36,198,177,0.10)',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  minHeight: 120,
                  padding: 0,
                  cursor: 'pointer',
                }}
                onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(36,198,177,0.18)'}
                onMouseOut={e => e.currentTarget.style.boxShadow = '0 4px 16px 0 rgba(36,198,177,0.10)'}
              >
                <CardContent style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 18,
                  padding: '26px 22px 10px 22px',
                  flexGrow: 1,
                  borderBottom: '1px solid #f1f5f9',
                  minHeight: 70,
                }}>
                  <div style={{
                    background: '#e0f7f4',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <FormatIcon style={{ fontSize: 22, color: '#24c6b1' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" style={{ fontWeight: 600, color: '#1e293b', marginBottom: 1, fontSize: 17, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {report.name || report.extendedAttributes?.templateName || 'Unnamed Report'}
                    </Typography>
                    {report.extendedAttributes?.templateName && (
                      <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#64748b', fontWeight: 400, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Template: {report.extendedAttributes.templateName}
                      </Typography>
                    )}
                  </div>
                </CardContent>
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 20px 18px 20px',
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleEdit(report)}
                    sx={{
                      borderColor: '#24c6b1',
                      color: '#24c6b1',
                      fontWeight: 500,
                      textTransform: 'none',
                      px: 2.5,
                      minWidth: 64,
                      fontSize: 15,
                      borderRadius: 20,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': { borderColor: '#189e91', color: '#189e91', background: '#f0fdfa' },
                    }}
                    aria-label="Edit"
                  >
                    <EditIcon sx={{ fontSize: 18, mr: 1 }} /> Edit
                  </Button>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth fullScreen={false}>
        <DialogTitle className="dialog-title" style={{ padding: '12px 36px 12px 36px', borderTopLeftRadius: 16, borderTopRightRadius: 16, background: '#fff' }}>
          <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
            Edit Report
          </Typography>
          {editState?.extendedAttributes?.templateName && (
            <Typography variant="body2" color="textSecondary">
              Template: {editState.extendedAttributes.templateName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent className="MuiDialogContent-root" style={{ background: '#f8fafc', minWidth: 400, borderRadius: 16, boxShadow: '0 4px 24px rgba(36,198,177,0.10)' }}>
          {editState && (
            <div className="edit-template-form">
              {/* Step 1: Edit Report Info */}
              <Card className="report-config-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
                    Step 1: Edit Report Info
                  </Typography>
                  <div className="report-name-section">
                    <Input
                      type="text"
                      label="Report Name"
                      value={editState.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditState({ ...editState, name: e.target.value })}
                      className="report-name-input"
                      placeholder={PLACEHOLDERS.REPORT_NAME}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Step 2: Edit Columns */}
              <Card className="column-headers-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
                    Step 2: Edit Columns {selectedColumns.length > 0 && `(${selectedColumns.length})`}
                  </Typography>
                  {errorMessage && (
                    <Typography color="error" style={{ fontWeight: 600 }}>{errorMessage}</Typography>
                  )}
                  <div className="selected-columns-grid">
                    {selectedColumns.map((col, idx) => (
                      <div
                        key={col.id}
                        className={`column-header-item ${dragOverIndex === idx ? "drag-over" : ""} ${col.hasError ? "has-error" : ""} ${expandedFormat === col.id ? "expanded" : ""}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, col.id)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, idx)}
                        onDragEnd={handleDragEnd}
                      >
                        {editingColumnNumber === idx ? (
                          <div className="column-number-editing">
                            <input
                              type="number"
                              value={newColumnPosition}
                              onChange={(e) => setNewColumnPosition(e.target.value)}
                              onBlur={handlePositionInputBlur}
                              onKeyDown={handlePositionInputKeyPress}
                              min={1}
                              max={selectedColumns.length}
                              className="column-number-input"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className="column-number" onClick={() => handleNumberClick(idx)} title="Click to change position">
                            {idx + 1}
                          </div>
                        )}
                        <div className="column-header-top">
                          <div className="column-left">
                            <div className="column-drag-handle" title="Drag to reorder">
                              <DragIndicator fontSize="small" />
                            </div>
                            <div className="column-info">
                              <Typography variant="body2" className="column-display-name">
                                {col.id}
                              </Typography>
                              {/* Column type chip */}
                              <Chip
                                label={getColumnType(col) || 'string'}
                                size="small"
                                style={{
                                  background: '#e0f7f4',
                                  color: '#24c6b1',
                                  fontWeight: 600,
                                  marginLeft: 8,
                                }}
                              />
                            </div>
                          </div>
                          <div className="column-right">
                            <Input
                              type="text"
                              placeholder="Custom Header"
                              value={col.customHeader}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleHeaderChange(col.id, e.target.value)}
                              className="header-input"
                              fullWidth
                              style={{ minWidth: 180, maxWidth: 320 }}
                            />
                            {(getColumnType(col) === 'date' || getColumnType(col) === 'number') && (
                              <div className="format-button" onClick={() => handleFormatExpand(col.id)}>
                                <IconButton size="small" className="format-icon" title="Format Settings">
                                  <FormatIcon fontSize="small" />
                                </IconButton>
                              </div>
                            )}
                            <IconButton size="small" onClick={() => handleRemoveColumn(col.id)} className="remove-column-icon" title="Remove Column">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                        {expandedFormat === col.id && (
                          <div className="column-format-controls">
                            {/* Date format dropdown */}
                            {getColumnType(col) === 'date' && (
                              <div className="format-control">
                                <div className="format-content">
                                  <FormControl size="small" className="format-select">
                                    <Select
                                      value={col.format}
                                      onChange={(e) => {
                                        const value = e.target.value as string;
                                        if (value === 'custom') {
                                          setShowCustomDateFormat(prev => ({ ...prev, [col.id]: true }));
                                          handleFormatChange(col.id, 'custom');
                                        } else {
                                          setShowCustomDateFormat(prev => ({ ...prev, [col.id]: false }));
                                          handleFormatChange(col.id, value);
                                        }
                                      }}
                                      label="Date Format"
                                    >
                                      {DATE_FORMAT_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          <div className="format-option">
                                            <span>{option.label}</span>
                                          </div>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {showCustomDateFormat[col.id] && (
                                    <div className="custom-format-container">
                                      <Input
                                        type="text"
                                        placeholder={PLACEHOLDERS.CUSTOM_DATE_FORMAT}
                                        value={customDateFormat[col.id] || col.format || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          setCustomDateFormat(prev => ({ ...prev, [col.id]: e.target.value }));
                                          handleFormatChange(col.id, e.target.value);
                                        }}
                                        className="custom-format-input"
                                        error={!!formatErrors[col.id]}
                                        helperText={formatErrors[col.id]}
                                      />
                                      <Tooltip title="Use yyyy, mm, dd, HH, MM, SS, A, Z and separators (-/.)">
                                        <IconButton size="small" className="format-help-icon">
                                          <InfoIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Number format dropdown */}
                            {getColumnType(col) === 'number' && (
                              <div className="format-control">
                                <div className="format-content">
                                  <FormControl size="small" className="format-select">
                                    <Select
                                      value={col.format}
                                      onChange={(e) => {
                                        const value = e.target.value as string;
                                        if (value === 'custom') {
                                          setShowCustomNumberFormat(prev => ({ ...prev, [col.id]: true }));
                                          handleFormatChange(col.id, 'custom');
                                        } else {
                                          setShowCustomNumberFormat(prev => ({ ...prev, [col.id]: false }));
                                          handleFormatChange(col.id, value);
                                        }
                                      }}
                                      label="Number Format"
                                    >
                                      {NUMBER_FORMAT_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          <div className="format-option">
                                            <span>{option.label}</span>
                                          </div>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {showCustomNumberFormat[col.id] && (
                                    <div className="custom-format-container">
                                      <Input
                                        type="text"
                                        placeholder={PLACEHOLDERS.CUSTOM_NUMBER_FORMAT}
                                        value={customNumberFormat[col.id] || col.format || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          setCustomNumberFormat(prev => ({ ...prev, [col.id]: e.target.value }));
                                          handleFormatChange(col.id, e.target.value);
                                        }}
                                        className="custom-format-input"
                                        error={!!formatErrors[col.id]}
                                        helperText={formatErrors[col.id]}
                                      />
                                      <Tooltip title="Use 0, #, ,, . for formatting">
                                        <IconButton size="small" className="format-help-icon">
                                          <InfoIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Fallback for other types */}
                            {getColumnType(col) !== 'date' && getColumnType(col) !== 'number' && (
                              <div className="format-control">
                                <div className="format-content">
                                  <Input
                                    type="text"
                                    placeholder="Format (string)"
                                    value={col.format}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormatChange(col.id, e.target.value)}
                                    className="custom-format-input"
                                    error={!!formatErrors[col.id]}
                                    helperText={formatErrors[col.id]}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Step 3: Add Column */}
              <Card className="add-column-section">
                <CardContent>
                  <FormControl fullWidth>
                    <Select
                      value={newColumnToAdd}
                      displayEmpty
                      renderValue={() => (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<AddIcon />}
                          className="add-column-btn"
                          fullWidth
                        >
                          Add Column
                        </Button>
                      )}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewColumnToAdd("");
                        if (value) handleAddColumn(value as string);
                      }}
                      disabled={templateLoading || getAvailableTemplateColumns().length === 0}
                      className="column-select"
                    >
                      <MenuItem value="">
                        <span style={{ color: '#94a3b8', fontWeight: 400 }}>Choose a column...</span>
                      </MenuItem>
                      {getAvailableTemplateColumns().map(({ key, meta }) => (
                        <MenuItem key={key} value={key}>
                          <span style={{ fontWeight: 500, color: '#1e293b' }}>{meta.label || key} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({meta.type || 'string'})</span></span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Step 4: Actions */}
              <div className="action-buttons">
                <Button onClick={() => setEditDialogOpen(false)} className="reset-btn" variant="outlined" style={{ fontWeight: 500, borderRadius: 8, fontSize: 16, padding: '8px 28px', marginRight: 8 }}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary" className="create-btn" style={{ fontWeight: 600, borderRadius: 8, fontSize: 16, padding: '8px 28px', marginLeft: 0, background: '#24c6b1', color: '#fff', boxShadow: '0 2px 8px rgba(36,198,177,0.10)' }}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageReportTemplateBased; 